import type { RouteRecordRaw } from "vue-router";
import { EPatientTabs } from "@/types/filter/tableFilters";
import {
  DASHBOARD_DESTINATIONS,
  DEFAULT_DASHBOARD_TAB,
} from "@/composables/models/patient/useDashboardDestinations";

/**
 * Paths that used to exist and were renamed. External links (bookmarks, links
 * in already-sent emails and notifications) still point at them, so each one
 * gets a redirect record instead of falling through to the 404 guard.
 *
 * This is deliberately limited to URLs people actually share or bookmark. Pages
 * reached only by in-app navigation don't need an entry — a rename there breaks
 * nothing, because the links pointing at them moved with the rename.
 *
 * Internal navigation must never rely on these — app code uses the typed route
 * names. Entries here are a compatibility layer for URLs we no longer control.
 *
 * Every target must be a route that still exists in `typed-router.d.ts`, and a
 * legacy `:param` must name the same entity as the one it maps onto.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  "/patients/:id": "/rooms/patient/:id",
  "/patient/:id": "/rooms/patient/:id",
};

/**
 * The old dashboard's tab slugs, from its `TAB_URL_MAP`. The tab was a path
 * segment (`/dashboard/referrals-in`) before the table was split into three
 * pages, where it became a query param on one of them.
 *
 * These resolve through `DASHBOARD_DESTINATIONS`, which already owns the
 * old-tab → new-page mapping for the stored dashboard preference, so a tab that
 * moves pages later only has to be updated there.
 */
const LEGACY_DASHBOARD_TABS: Record<string, EPatientTabs> = {
  all: EPatientTabs.ALL,
  "action-required": EPatientTabs.ACTION_REQUIRED,
  "treatment-plans": EPatientTabs.IN_TREATMENT_PLAN,
  "treatment-plan": EPatientTabs.IN_TREATMENT_PLAN,
  "in-treatment": EPatientTabs.IN_TREATMENT,
  "referrals-in": EPatientTabs.REFERRALS_IN,
  "referrals-out": EPatientTabs.REFERRALS_OUT,
  archived: EPatientTabs.ARCHIVED,
};

/**
 * Legacy paths are absent from the generated route map, so `route.params` is
 * typed as the union of params for routes that still exist. These records match
 * paths outside that union, and read their params by name.
 */
type TLegacyRoute = { params: Record<string, string | string[] | undefined> };

function readParam(route: TLegacyRoute, key: string): string | undefined {
  const value = route.params[key];
  return Array.isArray(value) ? value[0] : value;
}

function buildTarget(template: string, route: TLegacyRoute): string {
  return template.replaceAll(/:(\w+)/g, (match, key: string) => readParam(route, key) ?? match);
}

/**
 * Redirects the retired `/dashboard/:tab` paths onto the split dashboard. An
 * unrecognised slug lands on the default tab rather than a 404 — it was a
 * dashboard URL, so a dashboard is the useful destination.
 */
function buildDashboardRedirect(): RouteRecordRaw {
  return {
    path: "/dashboard/:tab",
    redirect: (route) => {
      const slug = readParam(route, "tab") ?? "";
      const legacyTab = LEGACY_DASHBOARD_TABS[slug] ?? DEFAULT_DASHBOARD_TAB;
      const { path, tab } = DASHBOARD_DESTINATIONS[legacyTab];

      return { path, query: { tab } };
    },
  };
}

/**
 * Builds redirect records for {@link LEGACY_REDIRECTS}. Vue Router carries the
 * query and hash over to the target on its own.
 */
export function buildLegacyRedirectRoutes(
  map: Record<string, string> = LEGACY_REDIRECTS
): RouteRecordRaw[] {
  return [
    ...Object.entries(map).map(([from, to]) => ({
      path: from,
      redirect: (route: TLegacyRoute) => buildTarget(to, route),
    })),
    buildDashboardRedirect(),
  ] satisfies RouteRecordRaw[];
}
